import * as React from 'react';
import { IgcRowIslandComponent as Component } from 'igniteui-webcomponents-grids/grids/index.js';
import type { IgcRowIslandComponentEventMap } from 'igniteui-webcomponents-grids/grids/index.js';
import { type EventName, createComponent } from '../react-props.js';

/**
 * Row island
 * @fires gridCreated - Event emitted when a grid is being created based on this row island.
 * @fires gridInitialized - Emitted after a grid is being initialized for this row island.  The emitting is done in `ngAfterViewInit`.
 * @fires dataPreLoad - Emitted when a new chunk of data is loaded from virtualization.
 * @fires filteringExpressionsTreeChange - Emitted after filtering is performed.
 * @fires advancedFilteringExpressionsTreeChange - Emitted after advanced filtering is performed.
 * @fires gridScroll - Emitted when grid is scrolled horizontally/vertically.
 * @fires cellClick - Emitted when a cell is clicked.
 * @fires rowClick - Emitted when a row is clicked.
 * @fires formGroupCreated - Emitted when formGroup is created on edit of row/cell.
 * @fires validationStatusChange - Emitted when grid's validation status changes.
 * @fires selected - Emitted when a cell is selected.
 * @fires rowSelectionChanging - Emitted when `IgxGridRowComponent` is selected.
 * @fires columnSelectionChanging - Emitted when `IgxColumnComponent` is selected.
 * @fires columnPin - Emitted before `IgxColumnComponent` is pinned.
 * @fires columnPinned - Emitted after `IgxColumnComponent` is pinned.
 * @fires cellEditEnter - Emitted when cell enters edit mode.
 * @fires cellEditExit - Emitted when cell exits edit mode.
 * @fires cellEdit - Emitted when cell has been edited.
 * @fires cellEditDone - Emitted after cell has been edited and editing has been committed.
 * @fires rowEditEnter - Emitted when a row enters edit mode.
 * @fires rowEdit - Emitted when exiting edit mode for a row.
 * @fires rowEditDone - Emitted after exiting edit mode for a row and editing has been committed.
 * @fires rowEditExit - Emitted when row editing is canceled.
 * @fires columnInit - Emitted when a column is initialized.
 * @fires columnsAutogenerated -
 * @fires sorting - Emitted before sorting expressions are applied.
 * @fires sortingDone - Emitted after sorting is completed.
 * @fires filtering - Emitted before filtering expressions are applied.
 * @fires filteringDone - Emitted after filtering is performed through the UI.
 * @fires rowAdded - Emitted when a row is added.
 * @fires rowDeleted - Emitted when a row is deleted.
 * @fires rowDelete - Emmited when deleting a row.
 * @fires rowAdd - Emmited just before the newly added row is commited.
 * @fires columnResized - Emitted after column is resized.
 * @fires contextMenu - Emitted when a cell or row is right clicked.
 * @fires doubleClick - Emitted when a cell is double clicked.
 * @fires columnVisibilityChanging - Emitted before column visibility is changed.
 * @fires columnVisibilityChanged - Emitted after column visibility is changed.
 * @fires columnMovingStart - Emitted when column moving starts.
 * @fires columnMoving - Emitted during the column moving operation.
 * @fires columnMovingEnd - Emitted when column moving ends.
 * @fires gridKeydown - Emitted when keydown is triggered over element inside grid's body.
 * @fires rowDragStart - Emitted when start dragging a row.
 * @fires rowDragEnd - Emitted when dropping a row.
 * @fires gridCopy - Emitted when a copy operation is executed.
 * @fires expansionStatesChange - Emitted when the rows are expanded or collapsed.
 * @fires selectedRowsChange - Emitted when the rows are selected or deselected.
 * @fires rowToggle - Emitted when the expanded state of a row gets changed.
 * @fires rowPinning - Emitted when the pinned state of a row is changed.
 * @fires rowPinned - Emitted when the pinned state of a row is changed.
 * @fires activeNodeChange - Emitted when the active node is changed.
 * @fires sortingExpressionsChange - Emitted before sorting is performed.
 * @fires toolbarExporting - Emitted when an export process is initiated by the user.
 * @fires rangeSelected - Emitted when making a range selection.
 * @fires rendered - Emitted after the ngAfterViewInit hook. At this point the grid exists in the DOM
 * @fires dataChanging - Emitted before the grid's data view is changed because of a data operation, rebinding, etc.
 * @fires dataChanged - Emitted after the grid's data view is changed because of a data operation, rebinding, etc.
 * @class
 */
export const IgrRowIsland = /* @__PURE__ */ createComponent({
  tagName: 'igc-row-island',
  displayName: 'IgrRowIsland',
  elementClass: Component,
  react: React,
  moveBackOnDelete: true,
  events: {
    onGridCreated: 'gridCreated' as EventName<
      IgcRowIslandComponentEventMap['gridCreated']
    >,
    onGridInitialized: 'gridInitialized' as EventName<
      IgcRowIslandComponentEventMap['gridInitialized']
    >,
    onDataPreLoad: 'dataPreLoad' as EventName<
      IgcRowIslandComponentEventMap['dataPreLoad']
    >,
    onFilteringExpressionsTreeChange:
      'filteringExpressionsTreeChange' as EventName<
        IgcRowIslandComponentEventMap['filteringExpressionsTreeChange']
      >,
    onAdvancedFilteringExpressionsTreeChange:
      'advancedFilteringExpressionsTreeChange' as EventName<
        IgcRowIslandComponentEventMap['advancedFilteringExpressionsTreeChange']
      >,
    onGridScroll: 'gridScroll' as EventName<
      IgcRowIslandComponentEventMap['gridScroll']
    >,
    onCellClick: 'cellClick' as EventName<
      IgcRowIslandComponentEventMap['cellClick']
    >,
    onRowClick: 'rowClick' as EventName<
      IgcRowIslandComponentEventMap['rowClick']
    >,
    onFormGroupCreated: 'formGroupCreated' as EventName<
      IgcRowIslandComponentEventMap['formGroupCreated']
    >,
    onValidationStatusChange: 'validationStatusChange' as EventName<
      IgcRowIslandComponentEventMap['validationStatusChange']
    >,
    onSelected: 'selected' as EventName<
      IgcRowIslandComponentEventMap['selected']
    >,
    onRowSelectionChanging: 'rowSelectionChanging' as EventName<
      IgcRowIslandComponentEventMap['rowSelectionChanging']
    >,
    onColumnSelectionChanging: 'columnSelectionChanging' as EventName<
      IgcRowIslandComponentEventMap['columnSelectionChanging']
    >,
    onColumnPin: 'columnPin' as EventName<
      IgcRowIslandComponentEventMap['columnPin']
    >,
    onColumnPinned: 'columnPinned' as EventName<
      IgcRowIslandComponentEventMap['columnPinned']
    >,
    onCellEditEnter: 'cellEditEnter' as EventName<
      IgcRowIslandComponentEventMap['cellEditEnter']
    >,
    onCellEditExit: 'cellEditExit' as EventName<
      IgcRowIslandComponentEventMap['cellEditExit']
    >,
    onCellEdit: 'cellEdit' as EventName<
      IgcRowIslandComponentEventMap['cellEdit']
    >,
    onCellEditDone: 'cellEditDone' as EventName<
      IgcRowIslandComponentEventMap['cellEditDone']
    >,
    onRowEditEnter: 'rowEditEnter' as EventName<
      IgcRowIslandComponentEventMap['rowEditEnter']
    >,
    onRowEdit: 'rowEdit' as EventName<IgcRowIslandComponentEventMap['rowEdit']>,
    onRowEditDone: 'rowEditDone' as EventName<
      IgcRowIslandComponentEventMap['rowEditDone']
    >,
    onRowEditExit: 'rowEditExit' as EventName<
      IgcRowIslandComponentEventMap['rowEditExit']
    >,
    onColumnInit: 'columnInit' as EventName<
      IgcRowIslandComponentEventMap['columnInit']
    >,
    onColumnsAutogenerated: 'columnsAutogenerated' as EventName<
      IgcRowIslandComponentEventMap['columnsAutogenerated']
    >,
    onSorting: 'sorting' as EventName<IgcRowIslandComponentEventMap['sorting']>,
    onSortingDone: 'sortingDone' as EventName<
      IgcRowIslandComponentEventMap['sortingDone']
    >,
    onFiltering: 'filtering' as EventName<
      IgcRowIslandComponentEventMap['filtering']
    >,
    onFilteringDone: 'filteringDone' as EventName<
      IgcRowIslandComponentEventMap['filteringDone']
    >,
    onRowAdded: 'rowAdded' as EventName<
      IgcRowIslandComponentEventMap['rowAdded']
    >,
    onRowDeleted: 'rowDeleted' as EventName<
      IgcRowIslandComponentEventMap['rowDeleted']
    >,
    onRowDelete: 'rowDelete' as EventName<
      IgcRowIslandComponentEventMap['rowDelete']
    >,
    onRowAdd: 'rowAdd' as EventName<IgcRowIslandComponentEventMap['rowAdd']>,
    onColumnResized: 'columnResized' as EventName<
      IgcRowIslandComponentEventMap['columnResized']
    >,
    onContextMenu: 'contextMenu' as EventName<
      IgcRowIslandComponentEventMap['contextMenu']
    >,
    onDoubleClick: 'doubleClick' as EventName<
      IgcRowIslandComponentEventMap['doubleClick']
    >,
    onColumnVisibilityChanging: 'columnVisibilityChanging' as EventName<
      IgcRowIslandComponentEventMap['columnVisibilityChanging']
    >,
    onColumnVisibilityChanged: 'columnVisibilityChanged' as EventName<
      IgcRowIslandComponentEventMap['columnVisibilityChanged']
    >,
    onColumnMovingStart: 'columnMovingStart' as EventName<
      IgcRowIslandComponentEventMap['columnMovingStart']
    >,
    onColumnMoving: 'columnMoving' as EventName<
      IgcRowIslandComponentEventMap['columnMoving']
    >,
    onColumnMovingEnd: 'columnMovingEnd' as EventName<
      IgcRowIslandComponentEventMap['columnMovingEnd']
    >,
    onGridKeydown: 'gridKeydown' as EventName<
      IgcRowIslandComponentEventMap['gridKeydown']
    >,
    onRowDragStart: 'rowDragStart' as EventName<
      IgcRowIslandComponentEventMap['rowDragStart']
    >,
    onRowDragEnd: 'rowDragEnd' as EventName<
      IgcRowIslandComponentEventMap['rowDragEnd']
    >,
    onGridCopy: 'gridCopy' as EventName<
      IgcRowIslandComponentEventMap['gridCopy']
    >,
    onExpansionStatesChange: 'expansionStatesChange' as EventName<
      IgcRowIslandComponentEventMap['expansionStatesChange']
    >,
    onSelectedRowsChange: 'selectedRowsChange' as EventName<
      IgcRowIslandComponentEventMap['selectedRowsChange']
    >,
    onRowToggle: 'rowToggle' as EventName<
      IgcRowIslandComponentEventMap['rowToggle']
    >,
    onRowPinning: 'rowPinning' as EventName<
      IgcRowIslandComponentEventMap['rowPinning']
    >,
    onRowPinned: 'rowPinned' as EventName<
      IgcRowIslandComponentEventMap['rowPinned']
    >,
    onActiveNodeChange: 'activeNodeChange' as EventName<
      IgcRowIslandComponentEventMap['activeNodeChange']
    >,
    onSortingExpressionsChange: 'sortingExpressionsChange' as EventName<
      IgcRowIslandComponentEventMap['sortingExpressionsChange']
    >,
    onToolbarExporting: 'toolbarExporting' as EventName<
      IgcRowIslandComponentEventMap['toolbarExporting']
    >,
    onRangeSelected: 'rangeSelected' as EventName<
      IgcRowIslandComponentEventMap['rangeSelected']
    >,
    onRendered: 'rendered' as EventName<
      IgcRowIslandComponentEventMap['rendered']
    >,
    onDataChanging: 'dataChanging' as EventName<
      IgcRowIslandComponentEventMap['dataChanging']
    >,
    onDataChanged: 'dataChanged' as EventName<
      IgcRowIslandComponentEventMap['dataChanged']
    >,
  },
  renderProps: {
    toolbarTemplate: 'toolbarTemplate',
    paginatorTemplate: 'paginatorTemplate',
    emptyGridTemplate: 'emptyGridTemplate',
    addRowEmptyTemplate: 'addRowEmptyTemplate',
    loadingGridTemplate: 'loadingGridTemplate',
    dragGhostCustomTemplate: 'dragGhostCustomTemplate',
    rowEditTextTemplate: 'rowEditTextTemplate',
    rowAddTextTemplate: 'rowAddTextTemplate',
    rowEditActionsTemplate: 'rowEditActionsTemplate',
    rowExpandedIndicatorTemplate: 'rowExpandedIndicatorTemplate',
    rowCollapsedIndicatorTemplate: 'rowCollapsedIndicatorTemplate',
    headerExpandedIndicatorTemplate: 'headerExpandedIndicatorTemplate',
    headerCollapsedIndicatorTemplate: 'headerCollapsedIndicatorTemplate',
    excelStyleHeaderIconTemplate: 'excelStyleHeaderIconTemplate',
    sortAscendingHeaderIconTemplate: 'sortAscendingHeaderIconTemplate',
    sortDescendingHeaderIconTemplate: 'sortDescendingHeaderIconTemplate',
    sortHeaderIconTemplate: 'sortHeaderIconTemplate',
    headSelectorTemplate: 'headSelectorTemplate',
    rowSelectorTemplate: 'rowSelectorTemplate',
    dragIndicatorIconTemplate: 'dragIndicatorIconTemplate',
  },
});

export type IgrRowIsland = Component;

/** @deprecated Module register is no longer needed and can be removed */
export const IgrRowIslandModule = Component;
